import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useDeliveryQueue } from '../hooks/useDeliveryQueue';
import { useDeliveryQueueJobs } from '../hooks/useDeliveryQueueJobs';
import DeliveryQueueTable from '../components/DeliveryQueueTable';
import DeliveryQueueProcessButton from '../components/DeliveryQueueProcessButton';
import DeliveryQueueJobStatus from '../components/DeliveryQueueJobStatus';
import DeliveryQueueActiveJobs from '../components/DeliveryQueueActiveJobs';
import { companiesService, productsService, type Company, type Product } from '../services/firebaseFunctions';
import { useAuth } from '../contexts/AuthContext';

const DeliveryQueuePage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('entregas_pendentes', 'editar');
  const {
    items,
    loading,
    error,
    companyId,
    setCompanyId,
    productId,
    setProductId,
    pendingCount,
    processAll,
    processing,
    processError,
    syncResult,
    asyncMeta,
    jobStatus,
    overlappingJobs,
  } = useDeliveryQueue();
  const {
    jobs: activeJobs,
    loading: activeJobsLoading,
    error: activeJobsError,
    cancelJob,
    cancellingJobId,
    cancelError,
    reload: reloadActiveJobs,
  } = useDeliveryQueueJobs();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  const resolveCompanyName = (id?: string) => {
    if (!id) return 'todas as empresas';
    return companies.find((c) => c.id === id)?.name ?? id;
  };

  const resolveProductName = (id?: string) => {
    if (!id) return 'todos os produtos';
    return products.find((p) => p.id === id)?.titulo ?? id;
  };

  const overlapWarning = useMemo(() => {
    if (overlappingJobs.length === 0) return null;
    const nameForCompany = (id?: string) => (id ? companies.find((c) => c.id === id)?.name ?? id : 'todas as empresas');
    const nameForProduct = (id?: string) => (id ? products.find((p) => p.id === id)?.titulo ?? id : 'todos os produtos');
    const descriptions = overlappingJobs.map(
      (job) => `${nameForCompany(job.filters.companyId)} / ${nameForProduct(job.filters.productId)}`,
    );
    return `Sobreposição parcial com job em andamento: ${descriptions.join('; ')}.`;
  }, [overlappingJobs, companies, products]);

  const handleProcess = async () => {
    await processAll();
    await reloadActiveJobs();
  };

  useEffect(() => {
    companiesService
      .getAll()
      .then(setCompanies)
      .catch(() => setCompanies([]));
  }, []);

  useEffect(() => {
    productsService
      .getAll()
      .then(setProducts)
      .catch(() => setProducts([]));
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} mb={1}>
        Entregas Pendentes
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {pendingCount} {pendingCount === 1 ? 'item pendente' : 'itens pendentes'} no filtro atual
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, alignItems: 'flex-end' }}>
        <Autocomplete<Company, false, false, false>
          size="small"
          sx={{ minWidth: 220 }}
          options={companies}
          value={companies.find((c) => c.id === companyId) ?? null}
          onChange={(_, newValue) => setCompanyId(newValue?.id ?? '')}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          noOptionsText="Nenhuma empresa encontrada"
          renderInput={(params) => (
            <TextField {...params} label="Empresa" placeholder="Todas" />
          )}
        />

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="delivery-queue-product-label">Produto</InputLabel>
          <Select
            labelId="delivery-queue-product-label"
            label="Produto"
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            {products.map((product) => (
              <MenuItem key={product.id} value={product.id}>
                {product.titulo}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <DeliveryQueueProcessButton
          pendingCount={pendingCount}
          processing={processing}
          onProcess={handleProcess}
          syncResult={syncResult}
          processError={processError}
          canManage={canManage}
          overlapWarning={overlapWarning}
        />
      </Box>

      <DeliveryQueueJobStatus asyncMeta={asyncMeta} jobStatus={jobStatus} />

      <DeliveryQueueActiveJobs
        jobs={activeJobs}
        loading={activeJobsLoading}
        error={activeJobsError}
        cancelError={cancelError}
        cancellingJobId={cancellingJobId}
        canManage={canManage}
        onCancel={cancelJob}
        resolveCompanyName={resolveCompanyName}
        resolveProductName={resolveProductName}
      />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && <Typography color="error">{error}</Typography>}

      {!loading && !error && items.length === 0 && (
        <Typography color="text.secondary">Nenhuma entrega pendente no filtro selecionado</Typography>
      )}

      {!loading && !error && items.length > 0 && <DeliveryQueueTable items={items} />}
    </Box>
  );
};

export default DeliveryQueuePage;
